import React from 'react';
import { Search } from 'lucide-react';
import { IntegrationLogFilters as Filters, IntegrationLogTab } from '../../../types';
import { GAME_PROVIDER_CODES, PAYMENT_PROVIDER_CODES } from './constants';
import { toDatetimeLocalValue, fromDatetimeLocalValue } from './utils';
import {
  Input as SharedInput,
  Button as SharedButton,
  Select as SharedSelect,
  TableFilterBar,
  TableFilterField,
  TableFilterGrid,
} from '@kioskgaming/ui';

interface IntegrationLogFiltersProps {
  tab: IntegrationLogTab;
  filters: Filters;
  onChange: (filters: Filters) => void;
  onSearch: () => void;
  isSearching?: boolean;
}

const IntegrationLogFiltersPanel: React.FC<IntegrationLogFiltersProps> = ({
  tab,
  filters,
  onChange,
  onSearch,
  isSearching,
}) => {
  const providerOptions =
    tab === 'payment'
      ? PAYMENT_PROVIDER_CODES
      : tab === 'game'
        ? GAME_PROVIDER_CODES
        : [];

  const update = (patch: Partial<Filters>) => {
    onChange({ ...filters, ...patch, page: 1 });
  };

  return (
    <TableFilterBar>
      <TableFilterGrid>
        <TableFilterField label="From">
          <SharedInput
            type="datetime-local"
            value={toDatetimeLocalValue(filters.from)}
            onChange={(e) => update({ from: fromDatetimeLocalValue(e.target.value) })}
          />
        </TableFilterField>
        <TableFilterField label="To">
          <SharedInput
            type="datetime-local"
            value={toDatetimeLocalValue(filters.to)}
            onChange={(e) => update({ to: fromDatetimeLocalValue(e.target.value) })}
          />
        </TableFilterField>

        {tab !== 'webhooks' && (
          <>
            <TableFilterField label="Provider">
              <SharedSelect
                selectSize="md"
                value={filters.providerCode || ''}
                onChange={(e) => update({ providerCode: e.target.value || undefined })}
                options={[
                  { value: '', label: 'All' },
                  ...providerOptions.map((code) => ({ value: code, label: code })),
                ]}
                hidePlaceholder
              />
            </TableFilterField>
            <TableFilterField label="API method">
              <SharedInput
                value={filters.apiMethod || ''}
                onChange={(e) => update({ apiMethod: e.target.value || undefined })}
                placeholder="recharge, createPayment…"
              />
            </TableFilterField>
            <TableFilterField label="Success">
              <SharedSelect
                selectSize="md"
                value={filters.success || ''}
                onChange={(e) => update({ success: e.target.value || undefined })}
                options={[
                  { value: '', label: 'All' },
                  { value: 'true', label: 'Success' },
                  { value: 'false', label: 'Failed' },
                ]}
                hidePlaceholder
              />
            </TableFilterField>
            <TableFilterField label="User ID">
              <SharedInput
                value={filters.userId || ''}
                onChange={(e) => update({ userId: e.target.value || undefined })}
                placeholder="UUID"
              />
            </TableFilterField>
            {tab === 'game' && (
              <TableFilterField label="Game provider ID">
                <SharedInput
                  value={filters.gameProviderId || ''}
                  onChange={(e) => update({ gameProviderId: e.target.value || undefined })}
                />
              </TableFilterField>
            )}
            <TableFilterField label="Transaction ID">
              <SharedInput
                value={filters.transactionId || ''}
                onChange={(e) => update({ transactionId: e.target.value || undefined })}
                placeholder="DEP_… / WD_…"
              />
            </TableFilterField>
            <TableFilterField label="Request URL contains">
              <SharedInput
                value={filters.requestUrl || ''}
                onChange={(e) => update({ requestUrl: e.target.value || undefined })}
              />
            </TableFilterField>
          </>
        )}

        {tab === 'webhooks' && (
          <>
            <TableFilterField label="Transaction ID">
              <SharedInput
                value={filters.transactionId || ''}
                onChange={(e) => update({ transactionId: e.target.value || undefined })}
              />
            </TableFilterField>
            <TableFilterField label="Order number">
              <SharedInput
                value={filters.orderNumber || ''}
                onChange={(e) => update({ orderNumber: e.target.value || undefined })}
              />
            </TableFilterField>
            <TableFilterField label="Status">
              <SharedInput
                value={filters.status || ''}
                onChange={(e) => update({ status: e.target.value || undefined })}
              />
            </TableFilterField>
            <TableFilterField label="Processed">
              <SharedSelect
                selectSize="md"
                value={filters.processed || ''}
                onChange={(e) => update({ processed: e.target.value || undefined })}
                options={[
                  { value: '', label: 'All' },
                  { value: 'true', label: 'Processed' },
                  { value: 'false', label: 'Unprocessed' },
                ]}
                hidePlaceholder
              />
            </TableFilterField>
          </>
        )}
      </TableFilterGrid>

      <div className="flex justify-end pt-2">
        <SharedButton
          type="button"
          onClick={onSearch}
          disabled={!filters.from || !filters.to || isSearching}
        >
          <Search className="h-4 w-4 mr-2" />
          Search
        </SharedButton>
      </div>
    </TableFilterBar>
  );
};

export default IntegrationLogFiltersPanel;
